import React from 'react';
import { Box, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { MasteryDots } from './MasteryDots';
import { QuizAnswerForm } from './QuizAnswerForm';
import { HelpExplanation } from './HelpExplanation';
import { VocabWord } from '../data/swedish';

interface QuizContentProps {
  currentWord: VocabWord | null;
  mode: 'normal' | 'reverse' | 'fill';
  isReverse: boolean;
  currentWordMastery: number;
  isHighlighted: boolean;
  userInput: string;
  isProcessing: boolean;
  feedback: { status: 'correct' | 'incorrect' | null; message: string };
  onInputChange: (val: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
  getFillSentence: (word: VocabWord) => string;
  onBack: () => void;
}

export const QuizContent: React.FC<QuizContentProps> = ({
  currentWord, mode, isReverse, currentWordMastery, isHighlighted,
  userInput, isProcessing, feedback, onInputChange, onSubmit, inputRef,
  getFillSentence,
}) => {
  if (!currentWord) return null;

  return (
    <Box sx={{ textAlign: 'center' }}>
      {feedback.status === 'correct' && (
        <CheckCircleIcon sx={{ position: 'absolute', top: 16, right: 16, fontSize: 40, color: 'success.main' }} />
      )}
      {feedback.status === 'incorrect' && (
        <ErrorIcon sx={{ position: 'absolute', top: 16, right: 16, fontSize: 40, color: 'error.main' }} />
      )}

      {mode === 'fill' ? (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ color: 'text.secondary', mb: 1 }}>
            Fill in the blank with the correct Swedish word:
          </Typography>
          <Typography
            variant="h3"
            sx={{ fontWeight: 'black', mb: 1.5, wordBreak: 'break-word', color: 'text.primary' }}
          >
            {getFillSentence(currentWord)}
          </Typography>
          {!isHighlighted && (
            <Typography variant="h6" sx={{ color: 'warning.main', fontStyle: 'italic', mb: 1 }}>
              English Sentence: &ldquo;{currentWord.sentenceTranslation || `I see ${currentWord.english}.`}&rdquo;
            </Typography>
          )}
          {!isHighlighted && (
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 'medium' }}>
              Clue: Translate &ldquo;{currentWord.english}&rdquo; to Swedish
            </Typography>
          )}
        </Box>
      ) : (
        <Box>
          <Typography variant="subtitle1" sx={{ color: 'text.secondary', mb: 1 }}>
            {isReverse ? 'Translate this English word/phrase:' : 'Translate this Swedish word/phrase:'}
          </Typography>
          <Typography
            variant="h3"
            sx={{ fontWeight: 'black', mb: 1, wordBreak: 'break-word', color: 'text.primary' }}
          >
            {isReverse ? currentWord.english : currentWord.swedish}
          </Typography>
          {!isReverse && !isHighlighted && (
            <Typography variant="h6" sx={{ color: 'warning.main', fontStyle: 'italic', mb: 3 }}>
              Pronounced: [{currentWord.pronunciation}]
            </Typography>
          )}
        </Box>
      )}

      <MasteryDots mastery={currentWordMastery} highlighted={isHighlighted} />

      <QuizAnswerForm
        value={userInput}
        onChange={onInputChange}
        disabled={isProcessing}
        onSubmit={onSubmit}
        placeholder={
          mode === 'normal'
            ? 'Type the English translation...'
            : mode === 'reverse'
            ? 'Type the Swedish translation...'
            : 'Type the missing Swedish word...'
        }
        highlighted={isHighlighted}
        inputRef={inputRef}
      />

      {feedback.message && (
        <Typography
          variant="h6"
          sx={{ mt: 3, fontWeight: 'bold', color: feedback.status === 'correct' ? 'success.main' : 'error.main' }}
        >
          {feedback.message}
        </Typography>
      )}

      {!isHighlighted && (
        <HelpExplanation word={currentWord.swedish} />
      )}
    </Box>
  );
};
